import React, { useId } from 'react';
import Box from '@mui/material/Box';
import FilledInput from '@mui/material/FilledInput';
import FormControl from '@mui/material/FormControl';
import NativeSelect from '@mui/material/NativeSelect';
import Typography from '@mui/material/Typography';
import type { CSSObject, Theme } from '@mui/material/styles';
import { styled } from 'sensortower-components/src/utils/styled';
import { FormFieldProps, FormFieldValue } from './Form.types';
import { getLocalizedOptionLabel } from './utils/localization';

const isTextarea = (inputType?: string) => inputType === 'textarea';
const isDropdown = (inputType?: string) => inputType === 'dropdown';
const isHidden = (inputType?: string) => inputType === 'hidden';

type ControlProps = {
  field: FormFieldProps;
  fieldId: string;
  locale?: string;
  ariaDescribedBy?: string;
  defaultValue?: FormFieldValue;
  onValidate: (fieldName: string, value: FormFieldValue) => void;
};

// Every control below sits in the FieldControl of VisibleFormFieldControl, under its label, and gets
// aria-invalid from that FormControl's `error`. `required` and aria-describedby go through inputProps, which MUI
// spreads onto the native element after its own attributes: FormControl's `required` would also add
// MUI's asterisk to the label, next to the RequiredMark the label already carries. A field without a
// label sets hiddenLabel, so its text is centred rather than sitting below room kept for a label.

const DropdownControl = ({ field, fieldId, locale, ariaDescribedBy, defaultValue, onValidate }: ControlProps) => {
  const { label, fieldName, placeholder, isRequired, dropdownOptions, autoComplete } = field;

  return (
    <NativeSelect
      input={<FieldInput disableUnderline hiddenLabel={!label} />}
      inputProps={{
        id: fieldId,
        name: fieldName,
        autoComplete,
        required: isRequired,
        'aria-describedby': ariaDescribedBy,
      }}
      defaultValue={defaultValue ?? ''}
      onChange={(event) => fieldName && onValidate(fieldName, event.target.value)}
    >
      <option value="" disabled>
        {placeholder}
      </option>
      {/* `value` is the Salesforce picklist key and is never localized — only the label is. */}
      {dropdownOptions?.map((option) => (
        <option key={option} value={option}>
          {getLocalizedOptionLabel(option, locale)}
        </option>
      ))}
    </NativeSelect>
  );
};

const TextareaControl = ({ field, fieldId, ariaDescribedBy, defaultValue, onValidate }: ControlProps) => {
  const { label, fieldName, placeholder, isRequired, maxLength, autoComplete } = field;

  return (
    <FieldInput
      disableUnderline
      hiddenLabel={!label}
      // Grows with its text from three rows, so it needs no resize handle: TextareaAutosize would
      // overwrite a dragged height on the next keystroke.
      multiline
      minRows={3}
      id={fieldId}
      name={fieldName}
      autoComplete={autoComplete}
      placeholder={placeholder}
      inputProps={{ required: isRequired, maxLength, 'aria-describedby': ariaDescribedBy }}
      defaultValue={defaultValue}
      onChange={(event) => fieldName && onValidate(fieldName, event.target.value)}
    />
  );
};

const TextControl = ({ field, fieldId, ariaDescribedBy, defaultValue, onValidate }: ControlProps) => {
  const { label, fieldName, inputType, placeholder, isRequired, maxLength, autoComplete } = field;

  return (
    <FieldInput
      disableUnderline
      hiddenLabel={!label}
      id={fieldId}
      name={fieldName}
      type={inputType || 'text'}
      // Honoured by every control, not just the text input, so setting a token on a
      // field definition can never be silently dropped by the control it renders as.
      autoComplete={autoComplete}
      placeholder={placeholder}
      inputProps={{ required: isRequired, maxLength, 'aria-describedby': ariaDescribedBy }}
      defaultValue={defaultValue}
      onChange={(event) => fieldName && onValidate(fieldName, event.target.value)}
    />
  );
};

const renderControl = (props: ControlProps) => {
  const { inputType } = props.field;
  if (isDropdown(inputType)) return <DropdownControl {...props} />;
  if (isTextarea(inputType)) return <TextareaControl {...props} />;
  return <TextControl {...props} />;
};

type FormFieldControlProps = {
  field: FormFieldProps;
  formId?: string;
  index: number;
  locale?: string;
  error?: string;
  onValidate: (fieldName: string, value: FormFieldValue) => void;
  defaultValue?: FormFieldValue;
};

/**
 * No label or error slot: the page set this value, not the visitor. Reads the definition's `value`
 * so the known-visitor cookie cannot overwrite it, and is display:none, so it takes no grid cell.
 */
const HiddenControl = ({ field }: { field: FormFieldProps }) => (
  <input type="hidden" name={field.fieldName} value={field.value ?? ''} readOnly />
);

const VisibleFormFieldControl = ({
  field,
  formId,
  index,
  locale,
  error,
  onValidate,
  defaultValue,
}: FormFieldControlProps) => {
  const { label, fieldName, isRequired, helpText, fullWidth } = field;
  // useId keeps ids unique when several Form instances render on the same page
  const uid = useId();
  const fieldId = `${formId || 'form'}-${fieldName || index}-${uid}`;
  const describedById = helpText ? `${fieldId}-help` : undefined;
  const errorId = error ? `${fieldId}-error` : undefined;
  const ariaDescribedBy = [describedById, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <Field data-testid={`FormField-${field.id || index}`} fullWidth={fullWidth}>
      {/* The label sits small along the field's top edge, inside it, so the field never needs a
          line above it for the label (#1216). It stays there rather than floating up from the
          middle on focus: in the flow, a label too long for one line wraps and the field grows,
          where MUI's absolutely placed label would cut it off. */}
      <FieldControl variant="filled" size="small" fullWidth error={!!error}>
        {label && (
          <FieldLabel htmlFor={fieldId}>
            {label}
            {isRequired && <RequiredMark />}
          </FieldLabel>
        )}

        {renderControl({ field, fieldId, locale, ariaDescribedBy, defaultValue, onValidate })}
      </FieldControl>

      {helpText && <Caption id={describedById}>{helpText}</Caption>}
      {/* Always rendered, reserving one line, so an appearing error does not resize the field
          and shift everything below it — validation runs on every keystroke, so an unreserved
          error jitters the whole form while typing. FieldGrid's rowGap is reduced to match,
          since this reserved line now doubles as the gutter between rows. */}
      <ErrorSlot>
        {error && (
          <Caption id={errorId} role="alert" isError>
            {error}
          </Caption>
        )}
      </ErrorSlot>
    </Field>
  );
};

// A dispatcher, so the hidden branch never sits above VisibleFormFieldControl's hooks.
const FormFieldControl = (props: FormFieldControlProps) =>
  isHidden(props.field.inputType) ? <HiddenControl field={props.field} /> : <VisibleFormFieldControl {...props} />;

export default FormFieldControl;

// The Form's text size: control text and the opt-in disclaimer in Form.tsx.
export const FIELD_FONT_SIZE = 16;
export const FIELD_LINE_HEIGHT = 1.4;

const CAPTION_FONT_SIZE = 13;
const CAPTION_LINE_HEIGHT = 1.4;

const Field = styled(Box, {
  name: 'Form',
  slot: 'Field',
  shouldForwardProp: (prop) => prop !== 'fullWidth',
})<{ fullWidth?: boolean }>(({ fullWidth }) => ({
  display: 'flex',
  flexDirection: 'column',
  ...(fullWidth && { gridColumn: '1 / -1' }),
}));

// The field surface: label and control together. FilledInput's own fill and border would stop at the
// control, below the label.
const FieldControl = styled(FormControl, {
  name: 'Form',
  slot: 'Control',
})(({ theme, error }) => ({
  backgroundColor: theme.palette.field.main,
  borderRadius: 6,
  // The fill alone is 1.9:1 against the card; the border brings the field's edge to about 4:1, over
  // the 3:1 that WCAG 1.4.11 asks of a control's boundary. Inset, so it adds no height, and on a
  // layer above the control, so an autofilled input's fill cannot paint over it.
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: 'inherit',
    boxShadow: `inset 0 0 0 1px ${error ? theme.palette.error.light : theme.palette.grey[400]}`,
    pointerEvents: 'none',
  },
  // Around the label as well as the control. :has keeps it to keyboard focus on the select, as
  // :focus-visible on the element itself did.
  '&:has(:focus-visible)': focusRingStyles(theme),
}));

// 12px, the size of MUI's floated label. Its 4px top padding and 17px line make the 21px that MUI's
// small filled input keeps above its text for a label, so a one-line label leaves the field at 48px,
// the minimum touch target. The colour stays the same when focused or invalid: placeholder grey is
// 4.9:1 on the field, where teal and the error colour fall under the 4.5:1 a 12px label needs; the
// field's outline and the message below carry those states instead.
const FieldLabel = styled('label', {
  name: 'Form',
  slot: 'Label',
})(({ theme }) => ({
  display: 'block',
  padding: theme.spacing(0.5, 1.5, 0),
  fontSize: 12,
  lineHeight: '17px',
  textAlign: 'left',
  overflowWrap: 'break-word',
  color: theme.palette.placeholder.main,
}));

// Reserves one Caption line plus the 0.5 gutter it carries, so an appearing error never resizes
// what is around it. flow-root makes the slot a formatting context, so the Caption's top margin
// cannot collapse through it when the slot is empty — Form.tsx renders one in a plain block
// <form>, where that collapse would otherwise grow the card as the error appears.
export const ErrorSlot = styled(Box, {
  name: 'Form',
  slot: 'ErrorSlot',
})(({ theme }) => ({
  display: 'flow-root',
  minHeight: `calc(${CAPTION_FONT_SIZE}px * ${CAPTION_LINE_HEIGHT} + ${theme.spacing(0.5)})`,
}));

const RequiredMarkRoot = styled('span', {
  name: 'Form',
  slot: 'RequiredMark',
})(({ theme }) => ({
  color: theme.palette.error.light,
}));

// Also marks the opt-in checkbox in Form.tsx, so every required control gets the same cue. A
// no-break space joins it to the word before, so it never wraps onto a line of its own.
export const RequiredMark = () => <RequiredMarkRoot aria-hidden="true">{' *'}</RequiredMarkRoot>;

// The control every field shares — text inputs, the textarea and the select's input — on
// FieldControl's surface.
const FieldInput = styled(FilledInput, {
  name: 'Form',
  slot: 'Input',
})(({ theme, hiddenLabel }) => ({
  fontSize: FIELD_FONT_SIZE,
  fontFamily: 'inherit',
  color: theme.palette.common.white,
  borderRadius: 'inherit',
  // FilledInput has its own fill, darker on hover and focus; FieldControl paints one fill for the
  // whole field, and focus shows as its ring.
  '&, &:hover, &.Mui-focused': {
    backgroundColor: 'transparent',
  },
  // The label above already takes the room FilledInput keeps above the text for a label.
  ...(!hiddenLabel && {
    '&.MuiInputBase-multiline, & .MuiInputBase-input': { paddingTop: 0 },
  }),
  '& .MuiInputBase-input': {
    '&::placeholder': {
      color: theme.palette.placeholder.main,
      opacity: 1,
    },
    // Browsers paint autofilled fields in their own light colours, which cannot be overridden.
    // Clipped to the text, under the white text fill, that colour never shows, and the input stays
    // transparent on FieldControl's fill. Covering it with an inset shadow instead left a light
    // seam where the input meets the label, as the input starts on a fractional pixel.
    // Separate rules: a browser that does not know one selector would drop a list holding both.
    '&:-webkit-autofill': autofillStyles(theme),
    '&:autofill': autofillStyles(theme),
  },
  '& .MuiNativeSelect-select': {
    cursor: 'pointer',
    // NativeSelect rounds only its bottom corners while focused.
    borderRadius: 'inherit',
    // NativeSelect gives options the paper colour, white in this theme, under the field's white
    // text; browsers that style the open list (Chrome and Firefox off macOS) would show white on
    // white. The field's own colours, as the options inherited before NativeSelect.
    '&:not([multiple]) option, &:not([multiple]) optgroup': {
      backgroundColor: theme.palette.field.main,
      color: theme.palette.common.white,
    },
  },
  // The arrow centres itself in its positioned ancestor. Static here, the select's root hands that
  // to FieldControl, so the arrow centres on the whole field, label included, wrapped or not, rather
  // than on the value's line below the label.
  '&:has(.MuiNativeSelect-select)': {
    position: 'static',
  },
  '& .MuiNativeSelect-icon': {
    color: theme.palette.placeholder.main,
  },
}));

const autofillStyles = (theme: Theme): CSSObject => ({
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: theme.palette.common.white,
  caretColor: theme.palette.common.white,
});

/** The focus ring every Form control shares, native checkbox included. */
export const focusRingStyles = (theme: Theme): CSSObject => ({
  outline: `2px solid ${theme.palette.teal.main}`,
  outlineOffset: 1,
});

// Help text and errors under a field, and the opt-in error in Form.tsx. The `!important`s hold
// against ancestor rules like Section variants' `& .MuiTypography-body1` (0,2,0), which the
// wrapper's own class (0,1,0) cannot beat; line-height is pinned for the same reason, since a
// taller line would overflow the height ErrorSlot reserves.
export const Caption = styled(Typography, {
  name: 'Form',
  slot: 'Caption',
  shouldForwardProp: (prop) => prop !== 'isError',
})<{ isError?: boolean }>(({ theme, isError }) => ({
  fontSize: `${CAPTION_FONT_SIZE}px !important`,
  lineHeight: `${CAPTION_LINE_HEIGHT} !important`,
  color: `${isError ? theme.palette.error.light : theme.palette.placeholder.main} !important`,
  marginTop: theme.spacing(0.5),
}));
